import './style.scss'

import { ERROR, LOADING, READY, useVisualState } from '../../../../hooks/useVisualState'
import { Link, Route, Switch, useParams, withRouter } from 'react-router-dom'
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { Tab, Tabs } from '../../../../components/Tabs/Tabs'

import BannersFormModal from '../BannersFormModal'
import Button from '../../../../components/Button/Button'
import CircleLoader from '../../../../icons/animated/CircleLoader'
import FileFormModal from '../FileFormModal'
import Icon from '../../../../components/Icon/Icon'
import Modal from '../../../../components/Modal/Modal'
import ModalContent from '../../../../components/Modal/ModalContent'
import ModalFooter from '../../../../components/Modal/ModalFooter'
import ModalHeader from '../../../../components/Modal/ModalHeader'
import News from '../News/News'
import NewsFormModal from '../NewsFormModal'
import SiteFormModal from '../SiteFormModal'
import classNames from 'classnames'
import { get } from '../../../../helpers/request'
import t from '../../../../helpers/i18n'
import useObjectState from '../../../../hooks/useObjectState'

const ConfigurationTab = withRouter(({ history }) => {
  const [[siteState, site], setSite] = useVisualState()
  const [[competitionsState, competitions], setCompetitions] = useVisualState()
  const [updatingStatistics, setUpdatingStatistics] = useState(false)
  const [updateProgress, setUpdateProcess] = useObjectState()
  const { siteId } = useParams()

  const fetchData = useCallback(() => {
    setSite(LOADING)
    get('/soccer/sites/' + siteId)
      .then((response) => {
        setSite(READY, response)
      })
      .catch((error) => {
        setSite(ERROR, error)
      })
  }, [siteId, setSite])

  const handleClose = useCallback(
    (success) => {
      if (success) fetchData()
      history.push('/soccer/sites/' + siteId)
    },
    [fetchData, history, siteId]
  )

  useEffect(fetchData, [fetchData])

  const handleCloseUpdate = useCallback(() => setUpdatingStatistics(false), [])

  const handleUpdate = useCallback(
    (seasons) => {
      setUpdatingStatistics(true)
      setUpdateProcess({})

      Object.keys(seasons).forEach((season_id) =>
        setUpdateProcess(season_id, { name: seasons[season_id], success: null })
      )

      const season_ids = Object.keys(seasons)
      const run = (index = 0) => {
        if (index >= season_ids.length) return
        const season_id = season_ids[index]
        get('/soccer/seasons/' + season_id + '/statistics/update')
          .then(() =>
            setUpdateProcess(season_id, {
              name: seasons[season_id],
              success: true
            })
          )
          .catch(() =>
            setUpdateProcess(season_id, {
              name: seasons[season_id],
              success: false
            })
          )
          .finally(() => run(index + 1))
      }
      run()
    },
    [setUpdateProcess]
  )

  const handleUpdateAll = useCallback(() => {
    if (competitionsState !== READY) return
    const seasons = Object.keys(competitions).reduce((memo, competition_id) => {
      const competition = competitions[competition_id]
      Object.values(competition.seasons).forEach((season) => {
        memo[season.season_id] = competition.name + ' - ' + season.name
      })
      return memo
    }, {})
    handleUpdate(seasons)
  }, [competitions, competitionsState, handleUpdate])

  useEffect(() => {
    setCompetitions(LOADING)
    get('/soccer/competitions/seasons/' + siteId)
      .then((response) => setCompetitions(READY, response))
      .catch((error) => setCompetitions(ERROR, error))
  }, [setCompetitions, siteId])

  return (
    <Fragment>
      {siteState === READY && (
        <Fragment>
          <div className='site-configuration -content'>
            <div className='-header'>
              <div className='-title'>{site.name}</div>
              <div className='-buttons'>
                <Button
                  onClick={() =>
                    history.push('/soccer/sites/' + siteId + '/edit')
                  }
                  label={t('buttons.Edit')}
                />
              </div>
            </div>
            <div className='-row'>
              <div className='-label text-label color-text'>{t('titles.Quick_View')}</div>
              <div className='-preview'>
                <div className='-box'>
                  <div className='-image'>
                    {site.shortcut_icon && (
                      <img
                        src={
                          process.env.REACT_APP_IMAGE_BASE_URL +
                          '/icons/' +
                          site.shortcut_icon
                        }
                        alt=''
                      />
                    )}
                  </div>
                  <div className='-texts'>
                    <div className='-title'>{site.page_title}</div>
                    <div className='-description'>{site.page_description}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className='-row'>
              <div className='-label text-label color-text'>
                {t('titles.Horizontal_logo_and_vertical')}
              </div>
              <div className='-logos'>
                <div className='-logo -horizontal'>
                  {site.logo_horizontal && (
                    <img
                      src={
                        process.env.REACT_APP_IMAGE_BASE_URL +
                        '/logos/' +
                        site.logo_horizontal
                      }
                      alt=''
                    />
                  )}
                </div>
                <div className='-logo -vertical'>
                  {site.logo_vertical && (
                    <img
                      src={
                        process.env.REACT_APP_IMAGE_BASE_URL +
                        '/logos/' +
                        site.logo_vertical
                      }
                      alt=''
                    />
                  )}
                </div>
              </div>
            </div>
            <div className='-row'>
              <div className='-label text-label color-text'>{t('titles.Domains')}</div>
              <div className='-domains'>
                {site.domain.split(',').map((domain, index) => (
                  <div key={index} className='-domain'>
                    {domain}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className='site-configuration -content'>
            <div className='-header'>
              <div className='-title'>
                {t('titles.Update_statistics_seasons')}
              </div>
              <div className='-buttons'>
                {competitionsState === READY &&
                  Object.keys(competitions).length > 0 && (
                    <Button
                      onClick={handleUpdateAll}
                      type='light'
                      color='red'
                      label={t('buttons.Update_all')}
                    />
                  )}
              </div>
            </div>
            {competitionsState === READY && (
              <div>
                {Object.keys(competitions).map((competition_id) => {
                  const competition = competitions[competition_id]
                  return (
                    <div key={competition_id}>
                      <div value={competition_id} style={{ marginTop: '16px' }}>
                        {competition.name}
                      </div>
                      {Object.keys(competition.seasons).map((season_id) => {
                        const season = competition.seasons[season_id]
                        return (
                          <div
                            key={season_id}
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <Button
                              type='light'
                              size='field-sized'
                              label={t('buttons.Update')}
                              onClick={() =>
                                handleUpdate({
                                  [season.season_id]:
                                    competition.name + ' - ' + season.name
                                })
                              }
                            />
                            <div style={{ padding: '16px 16px' }}>
                              {season.name}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
                {updatingStatistics && (
                  <Modal onCloseRequested={handleCloseUpdate}>
                    <ModalHeader title={t('modal_headers.Updating_statistics')} />
                    <ModalContent>
                      <div>
                        {Object.keys(updateProgress).map((season_id) => {
                          return (
                            <div
                              key={season_id}
                              className={classNames('-update-modal', {
                                '-success':
                                  updateProgress[season_id].success === true,
                                '-fail':
                                  updateProgress[season_id].success === false
                              })}
                            >
                              <div>
                                {updateProgress[season_id].success === null ? (
                                  <CircleLoader />
                                ) : updateProgress[season_id].success ? (
                                  <Icon size={14} icon='CheckmarkIcon' />
                                ) : (
                                  <Icon size={20} icon='CloseIcon' />
                                )}
                              </div>
                              {updateProgress[season_id].name}
                              {updateProgress[season_id].success === false && (
                                <div className='caption-tiny-basic-italic -error'>
                                  {t('validations.It_happened_a_problem')}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </ModalContent>
                    <ModalFooter>
                      <Button
                        onClick={handleCloseUpdate}
                        label={t('buttons.Close')}
                        type='dark'
                        color='green'
                        grow
                      />
                    </ModalFooter>
                  </Modal>
                )}
              </div>
            )}
          </div>
        </Fragment>
      )}
      <Route exact path='/soccer/sites/:siteId/edit'>
        <SiteFormModal onCloseRequested={handleClose} />
      </Route>
    </Fragment>
  )
})

const NewsTab = withRouter(({ history }) => {
  const [[newsState, news], setNews] = useVisualState()
  const { siteId } = useParams()

  const fetchData = useCallback(() => {
    setNews(LOADING)
    get('/soccer/sites/' + siteId + '/news')
      .then((response) => {
        setNews(READY, response)
      })
      .catch((error) => {
        setNews(ERROR, error)
      })
  }, [siteId, setNews])

  const handleClose = useCallback(
    (success) => {
      if (success) fetchData()
      history.push('/soccer/sites/' + siteId + '/news')
    },
    [fetchData, history, siteId]
  )

  useEffect(fetchData, [fetchData])

  return (
    <div className='site-news'>
      {newsState === READY && (
        <Fragment>
          <div className='-header'>
            <div className='-title'>{t('titles.News')}</div>
            <div className='-buttons'>
              <Button
                onClick={() =>
                  history.push('/soccer/sites/' + siteId + '/news/create')
                }
                label={t('buttons.Create_news')}
              />
            </div>
          </div>
          {news.length === 0 && <div>{t('empty_descriptions.No_news')}.</div>}
          <div className='-news'>
            {news.map((aNew) => (
              <News
                key={aNew.site_news_id}
                id={aNew.site_news_id}
                title={aNew.title}
                content={aNew.content}
                coverImage={aNew.cover_image}
                tags={aNew.tags}
                created={aNew.modified}
                preview
              />
            ))}
          </div>
        </Fragment>
      )}
      <Route exact path='/soccer/sites/:siteId/news/:siteNewsId'>
        <NewsFormModal onCloseRequested={handleClose} />
      </Route>
    </div>
  )
})

const FilesTab = withRouter(({ history }) => {
  const [[filesState, files], setFiles] = useVisualState()
  const { siteId } = useParams()

  const fetchData = useCallback(() => {
    setFiles(LOADING)
    get('/soccer/sites/' + siteId + '/files')
      .then((response) => {
        setFiles(READY, response)
      })
      .catch((error) => {
        setFiles(ERROR, error)
      })
  }, [siteId, setFiles])

  const handleClose = useCallback(
    (success) => {
      if (success) fetchData()
      history.push('/soccer/sites/' + siteId + '/files')
    },
    [fetchData, history, siteId]
  )

  useEffect(fetchData, [fetchData])

  return (
    <div className='site-files'>
      {filesState === READY && (
        <Fragment>
          <button
            onClick={() =>
              history.push('/soccer/sites/' + siteId + '/files/create')
            }
          >
            {t('buttons.Create_file')}
          </button>
          <ul>
            {files.map((file) => (
              <li key={file.site_file_id}>
                <Link
                  to={'/soccer/sites/' + siteId + '/files/' + file.site_file_id}
                >
                  {file.title} ({file.status})
                </Link>
              </li>
            ))}
          </ul>
        </Fragment>
      )}
      <Route exact path='/soccer/sites/:siteId/files/:siteFileId'>
        <FileFormModal onCloseRequested={handleClose} />
      </Route>
    </div>
  )
})

const BannersTab = withRouter(({ history }) => {
  const [[bannersState, banners], setBanners] = useVisualState()
  const { siteId } = useParams()

  const fetchData = useCallback(() => {
    setBanners(LOADING)
    get('/soccer/sites/' + siteId + '/banners')
      .then((response) => {
        setBanners(READY, response)
      })
      .catch((error) => {
        setBanners(ERROR, error)
      })
  }, [siteId, setBanners])

  const handleClose = useCallback(
    (success) => {
      if (success) fetchData()
      history.push('/soccer/sites/' + siteId + '/banners')
    },
    [fetchData, history, siteId]
  )

  useEffect(fetchData, [fetchData])

  return (
    <div className='site-sponsors'>
      {bannersState === READY && (
        <Fragment>
          <button
            onClick={() =>
              history.push('/soccer/sites/' + siteId + '/banners/create')
            }
          >
            {t('buttons.Upload_ads')}
          </button>
          <ul>
            {banners.map((banner) => (
              <li key={banner.site_banner_id}>
                <Link
                  to={
                    '/soccer/sites/' +
                    siteId +
                    '/banners/' +
                    banner.site_banner_id
                  }
                >
                  {banner.link} ({banner.status})
                </Link>
              </li>
            ))}
          </ul>
        </Fragment>
      )}
      <Route exact path='/soccer/sites/:siteId/banners/:siteBannerId'>
        <BannersFormModal onCloseRequested={handleClose} />
      </Route>
    </div>
  )
})

const SiteView = () => {
  const { siteId } = useParams()

  return (
    <div className='site-view'>
      <Tabs>
        <Tab
          to={'/soccer/sites/' + siteId}
          title={t('menu.Site_Settings')}
          icon='InformationIcon'
        />
        <Tab
          to={'/soccer/sites/' + siteId + '/news'}
          title={t('menu.News')}
          icon='InformationIcon'
        />
        <Tab
          to={'/soccer/sites/' + siteId + '/files'}
          title={t('menu.Files')}
          icon='InformationIcon'
        />
        <Tab
          to={'/soccer/sites/' + siteId + '/banners'}
          title={t('menu.Sponsors')}
          icon='InformationIcon'
        />
      </Tabs>
      <Switch>
        <Route path='/soccer/sites/:siteId/news'>
          <NewsTab />
        </Route>
        <Route path='/soccer/sites/:siteId/files'>
          <FilesTab />
        </Route>
        <Route path='/soccer/sites/:siteId/banners'>
          <BannersTab />
        </Route>
        <Route path='/soccer/sites/:siteId'>
          <ConfigurationTab />
        </Route>
      </Switch>
    </div>
  )
}

export default SiteView
